import React from 'react';
import GenericSelectBox from '../generic/GenericSelectBox';
import { lensPath, set, merge, clone, path } from 'ramda';
import { conditionTypes, pageConditionsBoilerplate, systemValues } from '../../constants';
import { Button } from 'react-bootstrap';
import translations from '../../translations';
import '../../../css/page/page-conditions.less';

const BOOLEAN_SELECT_VALUES = {
    FALSE: '0',
    TRUE: '1',
};

/**
 * @param {Component} initialComponent The component to render just above the boolean page condition tool
 * @param {Array} components Array of objects representing page components currently in model
 * @param {Object} metaData Response from element/page endpoint
 * @param {Function} onApply Callback to parent component for making update call to api
 * @param {Function} showValidationError Callback to parent component to show there is a validation error
 * @param {Function} onCancel Callback to parent component for cancelling out of the creation process
 * @param {Object} selectedComponent The currently selected component that activates the condition
 * @description Component for creating simple boolean page conditions
 */
class BooleanPageCondition extends React.Component {

    constructor() {
        super();
        this.state = {
            metaData: null,
            ruleValueReference: '',
            operationComponent: '',
            booleanReference: '',
            metaDataType: '',
            select: '',
        };

        // The lens paths correspond to the various page metadata key values
        // that need to be modified in order to configure the page condition
        this.metaDataTypePath = lensPath([
            'pageConditions', 0,
            'pageOperations', 0,
            'assignment',
            'assignee',
            'metadataType',
        ]);
        this.assigneeComponentPath = lensPath([
            'pageConditions', 0,
            'pageOperations', 0,
            'assignment',
            'assignee',
            'pageObjectReferenceId',
        ]);
        this.pageOpValueRefPath = lensPath([
            'pageConditions', 0,
            'pageOperations', 0,
            'assignment',
            'assignor',
            'valueElementToReferenceId',
            'id',
        ]);
        this.pageRuleValueRefPath = lensPath([
            'pageConditions', 0,
            'pageRules', 0,
            'right',
            'valueElementToReferenceId',
            'id',
        ]);
        this.pageRuleLeftValueRefPath = lensPath([
            'pageConditions', 0,
            'pageRules', 0,
            'left',
            'valueElementToReferenceId',
        ]);
        this.leftRuleComponentPath = lensPath([
            'pageConditions', 0,
            'pageRules', 0,
            'left',
            'pageObjectReferenceId',
        ]);
    }

    componentDidMount() {
        let propsToState = this.props.metaData;
        if (this.props.metaData.pageConditions === null) {

            // By default the page conditions properties default value is null
            propsToState = merge(this.props.metaData, { pageConditions: [] });
        }

        // As there is not a way to easily just create a new page condition
        // with the api, some default meta data that the engine expects needs
        // to be manually injected into the page conditions metadata array
        propsToState.pageConditions.unshift(pageConditionsBoilerplate);
        this.setState({
            metaData: propsToState,
            ruleValueReference: this.getRuleComponentAssocValue(
                this.props.selectedComponent.id,
            ),
        });
    }

    onApply = () => {
        if (
            this.state.booleanReference &&
            this.state.operationComponent &&
            this.state.metaDataType
        ) {
            const pageRuleValueRef = this.state.booleanReference === '1' ? systemValues.TRUE.id : systemValues.FALSE.id;

            const metaDataMods = [
                { lens: this.leftRuleComponentPath, value: this.props.selectedComponent.id }, // Rule component
                { lens: this.pageRuleValueRefPath, value: pageRuleValueRef }, // Rule right value
                { lens: this.pageRuleLeftValueRefPath, value: this.state.ruleValueReference }, // Rule left value
                { lens: this.assigneeComponentPath, value: this.state.operationComponent }, // Operation component
                { lens: this.pageOpValueRefPath, value: systemValues.TRUE.id }, // Operation assignor value
                { lens: this.metaDataTypePath, value: this.state.metaDataType }, // Operation assignee metadata type
            ];
    
            const modifiedMetaData = this.updateMetaData(metaDataMods);
    
            this.setState({ metaData: modifiedMetaData }, () => {
                this.props.onApply(this.state.metaData);
            });

        } else {

            // Just for some really basic form validation, as the
            // response from the api isn't very user friendly
            // (if something goes disasterously wrong on the server side
            // then a response from the api will be displayed)
            this.props.showValidationError();
        }
    }

    updateBooleanReference = (e) => {
        const { value } = e.target;
        this.setState({ booleanReference: value });
    }

    updateOperationComponent = (e) => {
        const { value } = e.target;
        this.setState({ operationComponent: value });
    }

    updateMetaDataType = (e) => {
        const { value } = e.target;
        this.setState({ metaDataType: value });
    }

    getRuleComponentAssocValue = (componentId) => {

        // This is so that we can extract the components associated value id
        // which needs to be injected into the page condition meta data
        const valueProps = this.props.components.find(component => component.id.value === componentId)
            .valueElementValueBindingReferenceId.data;

        // Only do this if valueProps has an item
        if (path(['length'], valueProps) > 0) {
            const idValue = valueProps[0].properties.find(prop => prop.developerName === 'Id');

            const typeElementPropertyIdValue = valueProps[0].properties.find(prop => prop.developerName === 'TypeElementPropertyId');

            return {
                id: idValue.contentValue,
                typeElementPropertyId: (typeElementPropertyIdValue && typeElementPropertyIdValue.contentValue) || null,
            };
        }

        return null;
    }

    updateMetaData = (metaDataMods) => {
        let currentMetaData = this.state.metaData;
        metaDataMods.forEach((mod) => {
            const metaDataClone = clone(currentMetaData);
            currentMetaData = set(mod.lens, mod.value, metaDataClone);
        });
        return currentMetaData;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        let propsToState = nextProps.metaData;
        if (nextProps.metaData.pageConditions === null) {
            propsToState = merge(nextProps.metaData, { pageConditions: [] });
        }
        if (propsToState.pageConditions.length > 0) {

            // If a page condition has just been saved then a
            // dup of that condition is injected into the metadata
            // so as to correspond with the current options selected in the dropdowns
            const conditionJustSaved = nextProps.metaData.pageConditions[0];
            propsToState.pageConditions.unshift(conditionJustSaved);
        } else {
            propsToState.pageConditions.unshift(pageConditionsBoilerplate);
        }
        this.setState({ metaData: propsToState });
    }

    render() {

        const operationPageComponents = this.props.components.map(
            component => <option key={component.developerName.value} value={component.id.value}>{component.developerName.value}</option>,
        );

        const metaTypes = conditionTypes.map(type => <option key={type.key} value={type.value}>{type.key}</option>);

        operationPageComponents.unshift(
            <option key="default" value="" disabled hidden>-- Please Select --</option>,
        );

        metaTypes.unshift(
            <option key="default" value="" disabled hidden>-- Please Select --</option>,
        );

        return (
            <>
                <div className="overflow-y-auto">
                    {this.props.initialComponent}

                    <GenericSelectBox key="valueRef" required onFilter={this.updateBooleanReference} selected={this.state.select}>
                        <option key="default" value="" disabled hidden>-- Please Select --</option>
                        <option value={BOOLEAN_SELECT_VALUES.TRUE}>is true</option>
                        <option value={BOOLEAN_SELECT_VALUES.FALSE}>is false</option>
                    </GenericSelectBox>

                    <div className="flex align-center form-inline margin-top">
                        <span className="align-label">then make</span>
                        <GenericSelectBox key="assigneeComponent" required onFilter={this.updateOperationComponent} selected={this.state.select}>
                            {operationPageComponents}
                        </GenericSelectBox>

                        <GenericSelectBox key="metaDataType" required onFilter={this.updateMetaDataType} selected={this.state.select}>
                            {metaTypes}
                        </GenericSelectBox>
                    </div>
                </div>
                <div className="page-config-footer">
                    <Button bsStyle="default" className="btn-outline" onClick={this.props.onCancel}>Cancel</Button>
                    <Button bsStyle="primary" onClick={this.onApply}>
                        {
                            translations.PAGE_COND_BASIC_save
                        }   
                    </Button>
                </div>
            </>
        );
    }
}

export default BooleanPageCondition;
